import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Type } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const defaultLayout = () => ({
  name: "Untitled Layout",
  zones: [
    { id: cryptoId(), name: "Main", x: 0, y: 0, w: 70, h: 100 },
    { id: cryptoId(), name: "Side", x: 70, y: 0, w: 30, h: 100 },
  ],
});

function cryptoId() {
  return Math.random().toString(36).slice(2, 10);
}

const TEXT_DIRECTIONS = [
  { value: "left", label: "← Scroll Left" },
  { value: "right", label: "→ Scroll Right" },
  { value: "up", label: "↑ Scroll Up" },
  { value: "down", label: "↓ Scroll Down" },
  { value: "none", label: "Static (no scroll)" },
];

function ZonePreview({ zones }) {
  return (
    <div className="aspect-video bg-zinc-100 relative border border-zinc-300" data-testid="zone-preview">
      {zones.map((z, i) => (
        <div
          key={z.id}
          className="absolute border-2 border-[#002FA7] bg-[#002FA7]/5 flex items-center justify-center text-[10px] font-mono-disp uppercase tracking-wider text-[#002FA7]"
          style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` }}
        >
          {z.name || `Zone ${i + 1}`}
        </div>
      ))}
    </div>
  );
}

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

function InteractiveCanvas({ zones, selectedId, onSelect, onChange }) {
  const ref = useRef(null);
  const dragRef = useRef(null);

  const onPointerDown = (e, idx, mode) => {
    e.preventDefault(); e.stopPropagation();
    const rect = ref.current.getBoundingClientRect();
    dragRef.current = {
      idx, mode, rect,
      startX: e.clientX, startY: e.clientY,
      start: { ...zones[idx] },
    };
    onSelect(zones[idx].id);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
  };
  const onPointerMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const dx = ((e.clientX - d.startX) / d.rect.width) * 100;
    const dy = ((e.clientY - d.startY) / d.rect.height) * 100;
    let nz = { ...d.start };
    if (d.mode === "move") {
      nz.x = clamp(d.start.x + dx, 0, 100 - d.start.w);
      nz.y = clamp(d.start.y + dy, 0, 100 - d.start.h);
    } else if (d.mode === "resize") {
      nz.w = clamp(d.start.w + dx, 5, 100 - d.start.x);
      nz.h = clamp(d.start.h + dy, 5, 100 - d.start.y);
    }
    nz.x = Math.round(nz.x * 10) / 10;
    nz.y = Math.round(nz.y * 10) / 10;
    nz.w = Math.round(nz.w * 10) / 10;
    nz.h = Math.round(nz.h * 10) / 10;
    onChange(d.idx, nz);
  };
  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  return (
    <div
      ref={ref}
      className="aspect-video bg-zinc-100 relative border border-zinc-300 select-none touch-none"
      data-testid="layout-canvas"
      onPointerDown={() => onSelect(null)}
    >
      {zones.map((z, i) => {
        const sel = z.id === selectedId;
        return (
          <div
            key={z.id}
            data-testid={`canvas-zone-${i}`}
            onPointerDown={(e) => onPointerDown(e, i, "move")}
            className={`absolute flex items-center justify-center text-[10px] font-mono-disp uppercase tracking-wider cursor-move transition-colors ${
              sel
                ? "bg-[#002FA7]/20 border-2 border-[#002FA7] text-[#002FA7]"
                : "bg-[#002FA7]/5 border-2 border-[#002FA7]/60 text-[#002FA7]/80 hover:bg-[#002FA7]/10"
            }`}
            style={{ left: `${z.x}%`, top: `${z.y}%`, width: `${z.w}%`, height: `${z.h}%` }}
          >
            <span className="pointer-events-none">{z.name || `Zone ${i + 1}`}</span>
            <div
              data-testid={`resize-handle-${i}`}
              onPointerDown={(e) => onPointerDown(e, i, "resize")}
              className="absolute bottom-0 right-0 w-3 h-3 bg-[#002FA7] cursor-nwse-resize"
              title="Drag to resize"
            />
          </div>
        );
      })}
    </div>
  );
}

function Editor({ initial, onCancel, onSave }) {
  const [draft, setDraft] = useState(initial || defaultLayout());
  const [selId, setSelId] = useState(null);

  const updZone = (idx, patch) => {
    const zones = [...draft.zones];
    zones[idx] = { ...zones[idx], ...patch };
    // Set defaults when switching to text type
    if (patch.type === "text" && !zones[idx].text_content) {
      zones[idx].text_content = zones[idx].text_content || "Your running text here…";
      zones[idx].text_font_size = zones[idx].text_font_size || 32;
      zones[idx].text_color = zones[idx].text_color || "#FFFFFF";
      zones[idx].text_background = zones[idx].text_background || "#000000";
      zones[idx].text_speed = zones[idx].text_speed || 50;
      zones[idx].text_direction = zones[idx].text_direction || "left";
    }
    setDraft({ ...draft, zones });
  };
  const addZone = () => {
    const z = { id: cryptoId(), name: `Zone ${draft.zones.length + 1}`, x: 10, y: 10, w: 40, h: 40, type: "media" };
    setDraft({ ...draft, zones: [...draft.zones, z] });
    setSelId(z.id);
  };
  const rmZone = (idx) => setDraft({ ...draft, zones: draft.zones.filter((_, i) => i !== idx) });

  return (
    <div className="signage-card p-6 mb-6">
      <div className="flex items-center justify-between mb-5">
        <Input
          data-testid="layout-name-input"
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          className="text-xl font-display font-bold rounded-none max-w-md h-10"
        />
        <div className="flex gap-2">
          <button data-testid="layout-cancel" onClick={onCancel} className="signage-btn-secondary flex items-center gap-1"><X className="h-4 w-4" />Cancel</button>
          <button data-testid="layout-save" onClick={() => onSave(draft)} className="signage-btn-primary flex items-center gap-1"><Save className="h-4 w-4" />Save</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="label-eyebrow">Canvas</p>
            <p className="text-[10px] text-zinc-500">Drag to move · Drag the blue corner to resize</p>
          </div>
          <InteractiveCanvas
            zones={draft.zones}
            selectedId={selId}
            onSelect={setSelId}
            onChange={updZone}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="label-eyebrow">Zones</p>
            <button data-testid="add-zone-button" onClick={addZone} className="signage-btn-secondary flex items-center gap-1 text-xs">
              <Plus className="h-3 w-3" />Add zone
            </button>
          </div>
          <div className="space-y-3 max-h-[420px] overflow-auto pr-1">
            {draft.zones.map((z, i) => {
              const sel = z.id === selId;
              return (
              <div
                key={z.id}
                className={`border p-3 cursor-pointer transition-colors ${sel ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-200"}`}
                onClick={() => setSelId(z.id)}
                data-testid={`zone-row-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={z.name}
                    onChange={(e) => updZone(i, { name: e.target.value })}
                    className="rounded-none h-8 text-sm"
                    placeholder="Zone name"
                  />
                  <button onClick={(e) => { e.stopPropagation(); rmZone(i); }} className="p-1 hover:bg-red-50" data-testid={`remove-zone-${i}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>

                {/* Zone Type Selector */}
                <div className="mb-2">
                  <Label className="label-eyebrow text-[10px]">TYPE</Label>
                  <div className="flex gap-2 mt-1">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); updZone(i, { type: "media" }); }}
                      className={`flex-1 text-xs py-1.5 border transition-colors ${z.type === "media" || !z.type ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}
                    >
                      🖼 Media
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); updZone(i, { type: "text" }); }}
                      className={`flex-1 text-xs py-1.5 border transition-colors ${z.type === "text" ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"}`}
                    >
                      <Type className="h-3 w-3 inline mr-1" />Running Text
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-2">
                  {["x", "y", "w", "h"].map((k) => (
                    <div key={k}>
                      <Label className="label-eyebrow text-[10px]">{k.toUpperCase()}</Label>
                      <Input
                        type="number"
                        min="0" max="100"
                        value={z[k]}
                        onChange={(e) => updZone(i, { [k]: Number(e.target.value) })}
                        className="rounded-none h-8 text-sm mt-1"
                      />
                    </div>
                  ))}
                </div>

                {/* Running Text Fields — only shown when type=text */}
                {z.type === "text" && (
                  <div className="space-y-2 mt-2 border-t border-dashed border-zinc-200 pt-2">
                    <Label className="label-eyebrow text-[10px]">TEXT CONTENT</Label>
                    <textarea
                      value={z.text_content || ""}
                      onChange={(e) => updZone(i, { text_content: e.target.value })}
                      className="w-full border border-zinc-300 p-2 text-sm rounded-none h-16 resize-none focus:outline-none focus:border-[#002FA7]"
                      placeholder="Type your running text here…"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="label-eyebrow text-[10px]">FONT SIZE</Label>
                        <Input
                          type="number"
                          min="10" max="200"
                          value={z.text_font_size || 32}
                          onChange={(e) => updZone(i, { text_font_size: Number(e.target.value) })}
                          className="rounded-none h-8 text-sm mt-1"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div>
                        <Label className="label-eyebrow text-[10px]">SPEED</Label>
                        <Input
                          type="number"
                          min="10" max="200"
                          value={z.text_speed || 50}
                          onChange={(e) => updZone(i, { text_speed: Number(e.target.value) })}
                          className="rounded-none h-8 text-sm mt-1"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="label-eyebrow text-[10px]">TEXT COLOR</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={z.text_color || "#FFFFFF"}
                            onChange={(e) => updZone(i, { text_color: e.target.value })}
                            className="w-8 h-8 border-0 cursor-pointer p-0"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="text-[10px] text-zinc-500 font-mono">{z.text_color || "#FFFFFF"}</span>
                        </div>
                      </div>
                      <div>
                        <Label className="label-eyebrow text-[10px]">BACKGROUND</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <input
                            type="color"
                            value={z.text_background && z.text_background !== "transparent" ? z.text_background : "#000000"}
                            onChange={(e) => updZone(i, { text_background: e.target.value })}
                            className="w-8 h-8 border-0 cursor-pointer p-0"
                            disabled={z.text_background === "transparent"}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="text-[10px] text-zinc-500 font-mono">{z.text_background === "transparent" ? "Transparan" : z.text_background || "#000000"}</span>
                          <label className="flex items-center gap-1 text-[10px] cursor-pointer ml-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={z.text_background === "transparent"}
                              onChange={(e) => {
                                e.stopPropagation();
                                updZone(i, { text_background: e.target.checked ? "transparent" : "#000000" });
                              }}
                              className="accent-[#002FA7]"
                            />
                            Transparan
                          </label>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label className="label-eyebrow text-[10px]">DIRECTION</Label>
                      <select
                        value={z.text_direction || "left"}
                        onChange={(e) => updZone(i, { text_direction: e.target.value })}
                        className="w-full border border-zinc-300 p-1 text-sm rounded-none mt-1 focus:outline-none focus:border-[#002FA7]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {TEXT_DIRECTIONS.map((d) => (
                          <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layouts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null=none, "new" or layout obj

  const load = () => api.get("/layouts").then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (draft) => {
    try {
      if (draft.id) await api.put(`/layouts/${draft.id}`, { name: draft.name, zones: draft.zones });
      else await api.post("/layouts", { name: draft.name, zones: draft.zones });
      toast.success("Layout saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/layouts/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 sm:p-10">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="label-eyebrow">Layouts</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mt-2">Zone composer.</h1>
        </div>
        {!editing && (
          <button data-testid="new-layout-button" onClick={() => setEditing("new")} className="signage-btn-primary flex items-center gap-2">
            <Plus className="h-4 w-4" />New layout
          </button>
        )}
      </div>

      {editing && (
        <Editor
          initial={editing === "new" ? null : editing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => (
          <div key={l.id} className="signage-card p-5" data-testid={`layout-card-${l.id}`}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-display font-bold tracking-tight">{l.name}</h3>
              <span className="font-mono-disp text-[10px] text-zinc-500">{l.zones.length} zones</span>
            </div>
            <ZonePreview zones={l.zones} />
            <div className="flex gap-2 mt-3">
              <button data-testid={`edit-layout-${l.id}`} onClick={() => setEditing(l)} className="signage-btn-secondary text-xs flex-1">Edit</button>
              <button data-testid={`delete-layout-${l.id}`} onClick={() => remove(l.id)} className="signage-btn-secondary text-xs px-3">
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && !editing && (
          <div className="signage-card p-10 col-span-full text-center text-zinc-500">
            No layouts yet. Create your first to define how zones fill the screen.
          </div>
        )}
      </div>
    </div>
  );
}
